/**
 * Rule changes that turn wave 69 into the trivia fight.
 *
 * These are prototype patches rather than edits to the upstream mob classes, so the fork's
 * diff against OldSchoolSDK/InfernoTrainer stays reviewable and rebaseable. Importing this
 * module applies them; it must happen before InfernoRegion.initialiseRegion() builds the wave.
 */
import { TzKalZuk } from "../inferno/js/mobs/TzKalZuk";
import { Unit } from "osrs-sdk";
import type { Mob, Projectile } from "osrs-sdk";

/**
 * Upstream counts `setTimer` down here and spawns a JalZek + JalXil pair at zero (first at
 * 72 ticks, then every 350). Same method minus the timer block, so no sets ever arrive.
 */
TzKalZuk.prototype.attackIfPossible = function (this: TzKalZuk) {
  this.attackStyle = this.attackStyleForNewAttack();

  if (this.canAttack() && this.attackDelay <= 0) {
    this.attack() && this.didAttack();
  }
};

/**
 * Upstream uses damageTaken() to escalate the fight: pause the set timer below 600hp, spawn
 * Jad below 480, and spawn four JalMejJak healers below 240 (which also sets `enraged`,
 * dropping his attack speed from 10 ticks to 7).
 *
 * Only the death cleanup survives. Zuk never enrages, so his cadence stays at 10 ticks for
 * the whole fight, and nothing but Zuk and his shield is ever on the map.
 */
TzKalZuk.prototype.damageTaken = function (this: TzKalZuk) {
  if (this.currentStats.hitpoint <= 0) {
    this.region.mobs.forEach((mob: Mob) => {
      if ((mob as unknown) !== (this as unknown)) {
        mob.dying = 0;
      }
    });
  }
};

/**
 * Trivia answers are the only way to hurt Zuk.
 *
 * Two layers, because they cover different things. `canBeAttacked`/`selectable` are what
 * ZukShield uses, and they remove Zuk from click-targeting and the right-click menu — but
 * they do *not* stop damage: with aggro set another way the player's tbow still took him to
 * 1089 in testing. All damage ultimately arrives as a projectile, so that is the chokepoint.
 */
TzKalZuk.prototype.canBeAttacked = function () {
  return false;
};

Object.defineProperty(TzKalZuk.prototype, "selectable", {
  get() {
    return false;
  },
  configurable: true,
});

/** Marks the projectile the trivia layer fires, so it is let through the filter below. */
export const TRIVIA_HIT = "__triviaHit";

TzKalZuk.prototype.addProjectile = function (this: TzKalZuk, projectile: Projectile) {
  const isTrivia = (projectile as unknown as Record<string, unknown>)[TRIVIA_HIT] === true;
  if (projectile.damage > 0 && !isTrivia) return;
  Unit.prototype.addProjectile.call(this, projectile);
};

let lethalPatched = false;

/**
 * Make any hit that actually reaches the player fatal.
 *
 * Zuk's weapon is a private class inside TzKalZuk.ts, so this is reached through a live
 * instance rather than a static import. `registerProjectile` is the right seam: it runs
 * *after* `Weapon.attack()` has clamped damage to `min(target hp, roll, 100)`, so replacing
 * the damage here both bypasses that cap (lethal even above 100hp) and keeps Zuk's real
 * projectile — model, sound, flight delay and hitsplat all unchanged.
 *
 * Targeting is untouched: TzKalZuk.attack() still fires at the shield unless the player is
 * outside its 5-tile span or south of y=16, so staying in cover is still perfectly safe.
 */
export function makeZukHitsLethal(zuk: TzKalZuk): void {
  if (lethalPatched) return;
  const weapon = zuk.weapons["typeless"];
  if (!weapon) return;

  const proto = Object.getPrototypeOf(weapon);
  const original = proto.registerProjectile;

  proto.registerProjectile = function (from: Unit, to: Unit, ...rest: unknown[]) {
    if (to.isPlayer) {
      this.damage = Math.max(to.currentStats.hitpoint, 1);
    }
    return original.call(this, from, to, ...rest);
  };

  lethalPatched = true;
}
